import { Money } from '@shared/lib/shopify/types';
import { Cart } from '@shared/lib/shopify/types/shopify-native';
import { renderDiscount } from '@shared/lib/utils/discount-utils';
import { addMoney, subtractMoney, zeroMoney } from '@shared/lib/utils/money';
export function getCartBeforeDiscountTotal(cart: Cart): Money {
  return {
    amount: cart.lines
      .reduce((acc, line) => acc + parseFloat(line.cost.subtotalAmount.amount), 0)
      .toString(),
    currencyCode: 'USD',
  };
}

export function getCartAfterDiscountTotal(cart: Cart): Money {
  // Exclude partial payment discounts
  return subtractMoney(cart.cost.subtotalAmount, getCartDiscountsTotal(cart));
}

export function getCartDiscountsTotal(cart: Cart): Money {
  return {
    amount: cart.discountAllocations
      .reduce((acc, allocation) => acc + parseFloat(allocation.discountedAmount.amount), 0)
      .toString(),
    currencyCode: 'USD',
  };
}
export function getCartLevelDiscounts(cart: Cart): { title: string; totalAmount: Money }[] {
  const discounts = cart.discountAllocations.reduce(
    (acc, allocation) => {
      const titleOrCode = renderDiscount(allocation);
      if (titleOrCode) {
        if (!acc[titleOrCode]) {
          acc[titleOrCode] = zeroMoney;
        }
        acc[titleOrCode] = addMoney(acc[titleOrCode], allocation.discountedAmount);
      }
      return acc;
    },
    {} as Record<string, Money>
  );

  return Object.entries(discounts).map(([title, totalAmount]) => ({
    title,
    totalAmount,
  }));
}

export function getLineDiscountTotal(cart: Cart): Money {
  return {
    amount: cart.lines
      .reduce(
        (acc, line) =>
          acc +
          line.discountAllocations.reduce(
            (sum, allocation) => sum + parseFloat(allocation.discountedAmount.amount),
            0
          ),
        0
      )
      .toString(),
    currencyCode: cart.cost.totalAmount.currencyCode,
  };
}

export function getLineLevelDiscounts(cart: Cart): { title: string; totalAmount: Money }[] {
  const discounts = cart.lines.reduce(
    (acc, line) => {
      return line.discountAllocations.reduce((acc, allocation) => {
        const { title, code, discountedAmount } = allocation;
        const titleOrCode = title || code;
        if (titleOrCode) {
          if (!acc[titleOrCode]) {
            acc[titleOrCode] = {
              amount: 0,
              currencyCode: discountedAmount.currencyCode,
            };
          }
          acc[titleOrCode]!.amount += parseFloat(discountedAmount.amount);
        }
        return acc;
      }, acc);
    },
    {} as Record<string, { amount: number; currencyCode: string }>
  );

  return Object.keys(discounts).map((title) => ({
    title,
    totalAmount: {
      amount: discounts[title]!.amount.toFixed(2), // Convert the total amount back to a string
      currencyCode: discounts[title]!.currencyCode,
    },
  }));
}
